/**
 * Workflow API - Client pour les endpoints workflows
 */
import { api } from './api'

// =============================================================================
// Types
// =============================================================================

export interface Connector {
  id: number
  name: string
  slug: string
  description: string | null
  icon: string | null
  category: string
  connector_type: 'trigger' | 'action' | 'both'
  trigger_type: 'schedule' | 'poll' | 'webhook' | 'manual' | null
  config_schema: Record<string, any>
  credentials_schema: Record<string, any> | null
  requires_credentials: boolean
  is_active: boolean
  is_beta: boolean
}

export interface ConnectorConfig {
  id: number
  name: string
  connector_slug: string
  connector_name: string
  is_valid: boolean
  validation_error: string | null
  last_validated_at: string | null
  created_at: string
  updated_at: string | null
  credentials?: Record<string, any>  // Masked
}

export interface Workflow {
  id: number
  agent_id: number
  name: string
  description: string | null
  trigger_connector_id: number
  trigger_connector_slug: string | null
  trigger_connector_name: string | null
  trigger_user_config_id: number | null
  trigger_config: Record<string, any>
  action_connector_id: number
  action_connector_slug: string | null
  action_connector_name: string | null
  action_user_config_id: number | null
  action_config: Record<string, any>
  task_instructions: string | null
  variables: Record<string, any>
  webhook_token: string | null
  webhook_url: string | null
  is_enabled: boolean
  next_run_at: string | null
  last_run_at: string | null
  run_count: number
  success_count: number
  error_count: number
  consecutive_failures: number
  created_at: string
  updated_at: string | null
}

export interface WorkflowListItem {
  id: number
  name: string
  description: string | null
  agent_id: number
  trigger_connector_slug: string | null
  action_connector_slug: string | null
  is_enabled: boolean
  next_run_at: string | null
  last_run_at: string | null
  run_count: number
  success_count: number
  error_count: number
  failure_count?: number
  created_at: string
}

export interface WorkflowExecution {
  id: number
  workflow_id: number
  status: 'pending' | 'running' | 'success' | 'failed' | 'failure'
  error_message: string | null
  error_step: string | null
  trigger_data: Record<string, any> | null
  agent_input: string | null
  agent_output: string | null
  tokens_used: number | null
  action_result: Record<string, any> | null
  started_at: string
  completed_at: string | null
  duration_ms: number | null
  retry_count: number
  // Additional fields used by frontend
  input_data?: Record<string, any> | null
  output_data?: string | Record<string, any> | null
}

export interface AuditLog {
  id: number
  action: string
  user_id: number | null
  details: Record<string, any> | null
  ip_address: string | null
  created_at: string
}

export interface WorkflowStats {
  total: number
  enabled: number
  disabled: number
  // Additional fields used by frontend
  total_workflows?: number
  total_enabled?: number
  executions_today?: number
  success_rate?: number
}

// Alias pour compatibilité
export type WorkflowDetail = Workflow & {
  agent_name?: string
  failure_count?: number
}

export type WorkflowAuditLog = AuditLog & {
  details?: string
}

export interface WorkflowCreateData {
  agent_id: number
  name: string
  description?: string
  trigger_connector_slug: string
  trigger_config: Record<string, any>
  trigger_user_config_id?: number
  action_connector_slug: string
  action_config: Record<string, any>
  action_user_config_id?: number
  task_instructions?: string
  variables?: Record<string, any>
}

export interface WorkflowUpdateData {
  name?: string
  description?: string
  trigger_config?: Record<string, any>
  action_config?: Record<string, any>
  trigger_user_config_id?: number
  action_user_config_id?: number
  task_instructions?: string
  variables?: Record<string, any>
}

// Aliases for compatibility with older imports
export type WorkflowCreate = WorkflowCreateData
export type WorkflowUpdate = WorkflowUpdateData

// =============================================================================
// API Functions
// =============================================================================

export const workflowApi = {
  // Connectors
  listConnectors: async (type?: 'trigger' | 'action'): Promise<Connector[]> => {
    const params = type ? { connector_type: type } : {}
    const res = await api.get('/workflows/connectors', { params })
    return res.data
  },

  getConnector: async (slug: string): Promise<Connector> => {
    const res = await api.get(`/workflows/connectors/${slug}`)
    return res.data
  },

  // Connector Configs
  listConfigs: async (connectorSlug?: string): Promise<ConnectorConfig[]> => {
    const params = connectorSlug ? { connector_slug: connectorSlug } : {}
    const res = await api.get('/workflows/configs', { params })
    return res.data
  },

  createConfig: async (data: {
    connector_slug: string
    name: string
    credentials: Record<string, any>
  }): Promise<ConnectorConfig> => {
    const res = await api.post('/workflows/configs', data)
    return res.data
  },

  updateConfig: async (
    configId: number,
    data: { name?: string; credentials?: Record<string, any> }
  ): Promise<ConnectorConfig> => {
    const res = await api.patch(`/workflows/configs/${configId}`, data)
    return res.data
  },

  deleteConfig: async (configId: number): Promise<void> => {
    await api.delete(`/workflows/configs/${configId}`)
  },

  // Workflows
  listWorkflows: async (params?: {
    agent_id?: number
    is_enabled?: boolean
    limit?: number
    cursor?: number
  }): Promise<{ workflows: WorkflowListItem[]; next_cursor: number | null }> => {
    const res = await api.get('/workflows', { params })
    return res.data
  },

  getWorkflow: async (workflowId: number): Promise<Workflow> => {
    const res = await api.get(`/workflows/${workflowId}`)
    return res.data
  },

  createWorkflow: async (data: WorkflowCreateData): Promise<Workflow> => {
    const res = await api.post('/workflows', data)
    return res.data
  },

  updateWorkflow: async (
    workflowId: number,
    data: WorkflowUpdateData
  ): Promise<Workflow> => {
    const res = await api.patch(`/workflows/${workflowId}`, data)
    return res.data
  },

  deleteWorkflow: async (workflowId: number): Promise<void> => {
    await api.delete(`/workflows/${workflowId}`)
  },

  enableWorkflow: async (workflowId: number): Promise<Workflow> => {
    const res = await api.put(`/workflows/${workflowId}/enable`)
    return res.data
  },

  disableWorkflow: async (
    workflowId: number,
    reason?: string
  ): Promise<Workflow> => {
    const res = await api.put(`/workflows/${workflowId}/disable`, { reason })
    return res.data
  },

  getStats: async (): Promise<WorkflowStats> => {
    const res = await api.get('/workflows/stats')
    return res.data
  },

  // Executions
  listExecutions: async (
    workflowId: number,
    params?: { status?: string; limit?: number; cursor?: number }
  ): Promise<{ executions: WorkflowExecution[]; next_cursor: number | null; total_count: number; showing_last: number }> => {
    const res = await api.get(`/workflows/${workflowId}/executions`, { params })
    return res.data
  },

  getExecution: async (
    workflowId: number,
    executionId: number
  ): Promise<WorkflowExecution> => {
    const res = await api.get(`/workflows/${workflowId}/executions/${executionId}`)
    return res.data
  },

  // Audit
  getAuditTrail: async (
    workflowId: number,
    params?: { limit?: number; offset?: number }
  ): Promise<{ logs: AuditLog[]; total_count: number }> => {
    const res = await api.get(`/workflows/${workflowId}/audit`, { params })
    return res.data
  },

  // Trigger manuel
  triggerWorkflow: async (workflowId: number): Promise<{ execution_id: number }> => {
    const res = await api.post(`/workflows/${workflowId}/trigger`)
    return res.data
  },

  // Aliases pour compatibilité
  listAuditLogs: async (workflowId: number, limit?: number): Promise<WorkflowAuditLog[]> => {
    const res = await api.get(`/workflows/${workflowId}/audit`, { params: { limit } })
    return res.data.logs || []
  },

  // Delete execution
  deleteExecution: async (workflowId: number, executionId: number): Promise<void> => {
    await api.delete(`/workflows/${workflowId}/executions/${executionId}`)
  },

  // Flush all executions
  flushExecutions: async (workflowId: number): Promise<{ deleted_count: number }> => {
    const res = await api.delete(`/workflows/${workflowId}/executions`)
    return res.data
  },
}

export default workflowApi
